import { useState } from 'react';
import { grnApi, poApi } from '../api';
import { useData, useSubmit } from '../hooks/useData';
import { Spinner, Modal, PageHeader, Field, Empty } from '../components/common';
import toast from 'react-hot-toast';
import { getBDDate } from '../utils/dateUtils';
import { downloadReport } from '../utils/downloadUtils';

const EMPTY = {
  po_id: '', weight_of_package: '', delivery_charge: '0',
  other_cost: '0', received_date: getBDDate(),
  remarks: '', quantity: '', total_price: ''
};

export default function GrnPage() {
  const { data, loading, reload } = useData(grnApi.list);
  const { data: poList }          = useData(poApi.created);
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [selPo, setSelPo]         = useState(null);
  const [exporting, setExporting] = useState(false);

  const [doSubmit, saving] = useSubmit();

  const openAdd  = ()    => { setForm(EMPTY); setSelPo(null); setModal('add'); };
  const close    = ()    => { setModal(null); setSelPo(null); };
  const set      = e     => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadReport(grnApi.exportExcel, 'grn-list.xlsx');
    } catch {
      toast.error('Export failed — please try again');
    } finally {
      setExporting(false);
    }
  };

  // When PO is selected, auto-fill qty + price + freight/other-cost from the PO
  // (these are decided up-front on the PO now, via its Unit Cost Calculator —
  // no need to re-enter them here)
  const handlePoChange = e => {
    const id = e.target.value;
    const po = poList.find(p => String(p.id) === String(id));
    setSelPo(po || null);
    setForm(f => ({
      ...f,
      po_id:             id,
      quantity:          po ? String(po.total_qty)   : '',
      total_price:       po ? String(po.total_price) : '',
      weight_of_package: po ? (po.weight_of_package ?? '') : '',
      delivery_charge:   po ? String(po.delivery_charge ?? 0) : '0',
      other_cost:        po ? String(po.other_cost ?? 0) : '0',
    }));
  };

  const handleSave = () => {
    if (!form.po_id)    { toast.error('Please select a Purchase Order'); return; }
    if (!form.quantity) { toast.error('Quantity is required'); return; }
    if (modal === 'add') doSubmit(() => grnApi.create(form), 'GRN recorded — PO marked complete, item costs updated', reload, close);
    else                 doSubmit(() => grnApi.update(form.id, form), 'GRN updated', reload, close);
  };

  // Estimated cost per item — total_price already includes delivery + other
  // cost (they're baked into the PO's line prices at the PO stage), so they
  // are NOT added again here.
  const estCost = form.quantity && Number(form.quantity) > 0
    ? (Number(form.total_price || 0) / Number(form.quantity)).toFixed(2)
    : null;

  return (
    <div>
      <PageHeader title="Receive Goods (GRN)" subtitle="Record goods received against purchase orders"
        action={<button className="btn btn-primary btn-sm" onClick={openAdd}>+ New GRN</button>} />

      <div className="card">
        <div className="card-header">
          <span className="card-title">Received Goods</span>
          <button className="btn btn-secondary btn-sm" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
        {loading ? <Spinner /> : (
          <div className="table-wrap">
            {data.length === 0 ? <Empty msg="No GRN records yet" /> : (
              <table className="responsive-table">
                <thead><tr>
                  <th>PO #</th><th>Supplier / Brand</th>
                  <th>Qty</th><th>Total Cost</th><th>Received</th>
                </tr></thead>
                <tbody>{data.map(r => {
                  const totalCost = Number(r.total_price); // already includes delivery + other, set on the PO
                  return (
                    <tr key={r.id}>
                      <td data-label="PO #" className="td-primary font-mono">{r.po_number}</td>
                      <td data-label="Supplier / Brand">
                        <div>{r.supplier_name}</div>
                        <div className="td-muted">{r.brand_name}</div>
                      </td>
                      <td data-label="Qty" className="font-mono">{r.quantity}</td>
                      <td data-label="Total Cost">
                        <div className="font-mono">৳{totalCost.toLocaleString()}</div>
                        <div className="td-muted">incl. ৳{Number(r.delivery_charge).toLocaleString()} delivery, ৳{Number(r.other_cost).toLocaleString()} other</div>
                      </td>
                      <td data-label="Received" className="text-muted">{r.received_date}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {modal && (
        <Modal title={modal === 'add' ? 'New GRN Entry' : 'Edit GRN'}
          onClose={close} size="modal-lg"
          footer={<>
            <button className="btn btn-secondary" onClick={close}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save GRN'}</button>
          </>}>

          {/* PO selector */}
          <Field label="Purchase Order" required>
            <select name="po_id" value={form.po_id}
              onChange={modal === 'add' ? handlePoChange : set}
              disabled={modal === 'edit'}>
              <option value="">Select a PO (status: created)…</option>
              {poList.map(p => (
                <option key={p.id} value={p.id}>
                  {p.po_number} — {p.brand_name} ({p.supplier_name})
                </option>
              ))}
            </select>
          </Field>

          {/* Auto-filled PO info panel */}
          {selPo && (
            <div style={{
              marginBottom: 16, padding: '12px 16px',
              background: 'var(--c-accent-tint)',
              border: '1px solid var(--c-border)',
              borderLeft: '3px solid var(--c-accent)',
              borderRadius: 'var(--r-sm)', fontSize: '.85rem'
            }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--c-text)' }}>
                PO Details — values pre-filled below
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: '4px 20px', color: 'var(--c-text-2)' }}>
                <span><strong>Supplier:</strong> {selPo.supplier_name}</span>
                <span><strong>Brand:</strong> {selPo.brand_name}</span>
                <span><strong>PO Date:</strong> {selPo.po_date}</span>
                <span><strong>Original Qty:</strong> {selPo.total_qty}</span>
                <span><strong>Original Price:</strong> ৳{Number(selPo.total_price).toLocaleString()}</span>
                <span><strong>Package Weight:</strong> {selPo.weight_of_package ? `${selPo.weight_of_package} kg` : '—'}</span>
                <span><strong>Delivery Charge:</strong> ৳{Number(selPo.delivery_charge || 0).toLocaleString()}</span>
                <span><strong>Other Cost:</strong> ৳{Number(selPo.other_cost || 0).toLocaleString()}</span>
              </div>
              <div style={{ fontSize: '.74rem', color: 'var(--c-text-3)', marginTop: 8 }}>
                Freight and other cost were set on the Purchase Order — edit them there if they need to change.
              </div>
            </div>
          )}

          {/* Qty + Price — pre-filled from PO, editable */}
          <div className="form-row form-row-2">
            <Field label="Quantity Received">
              <input
                type="number" name="quantity"
                value={form.quantity}
                readOnly
                style={{ background: 'var(--c-bg)', cursor: 'not-allowed', color: 'var(--c-text-2)', fontWeight: 500 }}
              />
            </Field>
            <Field label="Total Price (৳)">
              <input
                type="number" name="total_price"
                value={form.total_price}
                readOnly
                style={{ background: 'var(--c-bg)', cursor: 'not-allowed', color: 'var(--c-text-2)', fontWeight: 500 }}
              />
            </Field>
          </div>

          <Field label="Received Date">
            <input type="date" name="received_date" value={form.received_date} onChange={set} />
          </Field>
          <Field label="Remarks">
            <textarea type="text" name="remarks" value={form.remarks || ''} onChange={set} rows={2} placeholder="Optional notes…" />
          </Field>

          {/* Perpetual Average Cost preview */}
          {estCost && (
            <div style={{
              marginTop: 12, padding: '14px 16px',
              background: 'var(--c-success-lt)',
              border: '1px solid rgba(26,122,74,.2)',
              borderRadius: 'var(--r-sm)',
              fontSize: '.875rem', color: 'var(--c-success)'
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Perpetual Average Cost after this GRN
              </div>
              <div style={{ color: 'var(--c-text-2)', fontSize: '.84rem', lineHeight: 1.8 }}>
                <strong>Landed cost per unit:</strong> ৳{estCost}
                <br />
                <span style={{ color: 'var(--c-text-3)' }}>
                  = ৳{form.total_price || 0} purchase total (already includes ৳{form.delivery_charge || 0} delivery + ৳{form.other_cost || 0} other, set on the PO)
                  ÷ {form.quantity} units
                </span>
                <br />
                <span style={{ fontSize: '.8rem', color: 'var(--c-text-3)', marginTop: 4, display: 'block' }}>
                  Final avg cost = (existing stock × existing cost + new qty × ৳{estCost}) ÷ (existing + new qty)
                </span>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}